export interface MetadataFieldProposal<T = any> {
  current: T;
  proposed: T;
  changed: boolean;
}

export interface ReleaseDateValue {
  year: string;
  release_date: string;
}

export interface GenreValue {
  genre1?: string;
  genre2?: string;
  genre3?: string;
}

export interface DiscTrackNumberValue {
  disc_number_spotify: number;
  track_number_spotify: number;
}

export interface NationalityValue {
  countryName: string;
  vef: string;
}

// Identifiants destinés à une association fiable avec des services externes (pas encore persistés
// nulle part, cf. discussion "champ caché + log" avant le futur envoi Winmedia). isrc vient de Deezer
// (import automatique) en priorité, sinon de Spotify (relance manuelle) ; artistId/recordingId ne sont
// remontés que si musicbrainzSearchTrack a une confiance suffisante (isrc ou score texte ≥ 0.55) —
// jamais depuis le repli homonyme-risqué par nom d'artiste seul.
export interface MusicbrainzIdsValue {
  isrc?: string;
  artistId?: string;
  recordingId?: string;
  matchConfidence?: string;
}

// Résumé d'un candidat Spotify parmi lesquels l'utilisateur peut choisir dans la modale de review
// (le mieux noté est sélectionné par défaut) : changer de candidat recalcule album/releaseDate/
// label/cover/discTrackNumber en conséquence (voir buildMetadataProposal).
export interface SpotifyCandidateSummary {
  index: number;
  score: number;
  artist: string;
  title: string;
  album: string;
  year: string;
  cover?: string;
  label?: string;
}

export interface MetadataProposal {
  title?: MetadataFieldProposal<string>;
  artist?: MetadataFieldProposal<string>;
  instagram?: MetadataFieldProposal<string>;
  label?: MetadataFieldProposal<string>;
  releaseDate?: MetadataFieldProposal<ReleaseDateValue>;
  album?: MetadataFieldProposal<string>;
  discTrackNumber?: MetadataFieldProposal<DiscTrackNumberValue>;
  cover?: MetadataFieldProposal<string>;
  genre?: MetadataFieldProposal<GenreValue>;
  artistImage?: MetadataFieldProposal<string>;
  similarArtist?: MetadataFieldProposal<string>;
  nationality?: MetadataFieldProposal<NationalityValue>;
  musicbrainzIds?: MetadataFieldProposal<MusicbrainzIdsValue>;
}

// Retour de buildMetadataProposal : le diff champ par champ, plus (si Spotify a matché) la liste des
// candidats parmi lesquels choisir et l'index actuellement retenu. Volontairement séparé de
// MetadataProposal pour ne pas casser le typage générique (proposal[field].proposed) utilisé partout
// ailleurs sur les champs du diff.
export interface MetadataProposalResult {
  proposal: MetadataProposal;
  spotifyCandidates?: SpotifyCandidateSummary[];
  spotifyCandidateIndex?: number;
}

// Champs à faible risque d'erreur : appliqués silencieusement, jamais affichés dans la modale de review.
// similarArtist y est aussi : applyMetadataProposal réinitialise music_advanced juste avant d'appliquer
// les résultats, donc sa "valeur actuelle" au moment du diff est toujours celle d'avant l'analyse — il
// ressortirait à tort comme "changé" à chaque fois, même quand la nouvelle recherche retrouve
// exactement la même valeur qu'avant.
export const METADATA_PROPOSAL_SILENT_FIELDS: (keyof MetadataProposal)[] = ['instagram', 'discTrackNumber', 'similarArtist', 'musicbrainzIds'];

// Champs affichés à l'utilisateur dans la modale de review manuelle.
export const METADATA_PROPOSAL_REVIEWABLE_FIELDS: (keyof MetadataProposal)[] = [
  'title', 'artist', 'album', 'releaseDate', 'label', 'cover', 'genre', 'nationality', 'artistImage'
];
